// Connect
import {
  getAppointmentsForFE,
  getPractitionerId,
  refreshGenieToken,
  resolveRecipientId,
  retryGenieOperationAfterReconnect,
} from '@/integrations/desktop/adaptors/genie/utils';
import type { GetScheduleMixin } from '@/integrations/desktop/mixins/GetScheduleMixin';
import type { IntegrationDesktopBaseAdaptor } from '@/integrations/desktop/mixins/IntegrationDesktopBaseAdaptor';
import type { OnboardingMixin } from '@/integrations/desktop/mixins/OnboardingMixin';
import type { SyncLettersMixin } from '@/integrations/desktop/mixins/SyncLettersMixin';
import type { SyncNotesMixin } from '@/integrations/desktop/mixins/SyncNotesMixin';
import { genieConnect, genieGetPractitioners } from '@/integrations/genie/api/connection';
import {
  genieGetTemplates,
  genieUploadDocument,
} from '@/integrations/genie/api/document';
import { looksLikeHtml } from '@/utils/looksLikeHtml';
import { CorrespondenceContentType, EHRProvider } from '@heidi/schema/ml-scribe';
import { formatConsultNoteForGenie, formatNoteForGenie } from './format-note-for-genie';

export { looksLikeHtml };

export const IntegrationsGenieDesktopAdaptor: IntegrationDesktopBaseAdaptor &
  SyncNotesMixin &
  GetScheduleMixin &
  SyncLettersMixin &
  OnboardingMixin = {
  ehrProvider: EHRProvider.Genie,

  doOnboarding: async (data) => {
    if (data.onboardingSession.current_step_number === 1) {
      // @ts-expect-error unknown for now - need to figure out typing later
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      const formData = data.data?.data.payload.form_data as {
        sender: string;
        password: string;
        instance_url: string;
      };

      let connectionId: string | null = null;

      try {
        connectionId = await genieConnect({
          instanceUrl: formData.instance_url,
          sender: formData.sender,
          password: formData.password,
        });
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'Failed to connect to Genie';
        return Promise.resolve({ status: 'error', data: message });
      }

      if (!connectionId) {
        return Promise.resolve({ status: 'error', data: 'Failed to connect' });
      }

      return Promise.resolve({ status: 'success', data: null });
    } else if (data.onboardingSession.current_step_number === 2) {
      const practitionerId = Number(
        // @ts-expect-error unknown for now - need to figure out typing later
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        data.data?.data.payload.form_data.doctor_id as number
      );

      const practitioners = await retryGenieOperationAfterReconnect({
        integrationOnboardingSession: data.onboardingSession,
        operation: genieGetPractitioners,
      });
      const practitionerIdsSet = new Set(practitioners.map((p) => p.id));

      if (!practitionerIdsSet.has(practitionerId)) {
        return Promise.resolve({ status: 'error', data: 'Doctor not found' });
      }

      return Promise.resolve({ status: 'success', data: null });
    } else {
      return Promise.resolve({ status: 'error', data: 'Unknown step' });
    }
  },

  getSchedule: async ({ startDate, endDate, integrationOnboardingSession }) => {
    const practitionerId = getPractitionerId(integrationOnboardingSession);

    try {
      const a = await getAppointmentsForFE(startDate, endDate, practitionerId);
      return a;
    } catch {
      await refreshGenieToken({
        integrationOnboardingSession,
      });

      return await getAppointmentsForFE(startDate, endDate, practitionerId);
    }
  },

  syncNotes: async (data) => {
    const practitionerId = getPractitionerId(data.integrationOnboardingSession);
    const patientId = data.session.patient_profile?.ehr_patient_id
      ? Number(data.session.patient_profile?.ehr_patient_id)
      : null;
    if (!patientId) {
      return { status: 'error', data: 'Patient ID not found' };
    }

    try {
      await genieUploadDocument({
        documentType: 'Consult',
        patientId,
        practitionerId,
        text: formatConsultNoteForGenie(data.session.consult_note.result ?? ''),
        signed: false,
        ...(data.session.created_time_local && {
          dateTime: data.session.created_time_local,
        }),
      });
      return { status: 'success', data: null };
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to upload note to Genie';
      return { status: 'error', data: message };
    }
  },

  getLetterTemplates: async () => {
    return await getLetterTemplates();
  },

  syncLetters: async (data) => {
    const practitionerId = getPractitionerId(data.integrationOnboardingSession);

    const patientId = data.session.patient_profile?.ehr_patient_id
      ? Number(data.session.patient_profile?.ehr_patient_id)
      : null;
    if (!patientId) {
      return { status: 'error', data: 'Patient ID not found' };
    }

    const letterTemplates = await getLetterTemplates();
    const template = letterTemplates.templates.find(
      (t) => t.document_id === data.templateId
    )?.name;

    if (!template) {
      return { status: 'error', data: 'Template not found' };
    }

    const isDocumentCorrespondence =
      data.correspondenceContentType === CorrespondenceContentType.document;
    const contentSource = isDocumentCorrespondence
      ? (data.documentContent ?? '')
      : (data.session.consult_note.result ?? '');

    // TODO: Revisit recipient resolution architecture:
    // 1. session.ehr_appt_id is deprecated — migrate to integrationLinkedSessionInfo
    //    which provides provider_appointment_details.unique_identifier.
    // 2. This makes an extra SOAP call (genieFindPatientAppointments) per letter sync.
    //    Consider caching appointment data or passing it through the session context.
    const recipientId =
      data.session.ehr_appt_id && data.session.created_time_local
        ? await resolveRecipientId(
            data.session.created_time_local,
            data.session.ehr_appt_id
          )
        : undefined;

    try {
      await genieUploadDocument({
        documentType: 'Letter',
        patientId,
        template,
        practitionerId,
        text: formatNoteForGenie(contentSource),
        signed: false,
        ...(recipientId != null && { recipientId }),
        ...(data.session.created_time_local && {
          dateTime: data.session.created_time_local,
        }),
      });
      return { status: 'success', data: null };
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to upload letter to Genie';
      console.error('IntegrationsGenieDesktopAdaptor.syncLetters', error);
      return { status: 'error', data: message };
    }
  },
};

const getLetterTemplates = async () => {
  const genieTemplates = await genieGetTemplates();

  return {
    templates: genieTemplates.map((name) => ({
      name,
      document_id: name,
    })),
  };
};
